import React, {useEffect, useMemo, useState} from "react"
import {get, keyBy, map} from "lodash"
import {BasePopup} from "uiKit"
import {BaseButton, SecondaryButton} from "components/buttons/Button"
import ProgramSelect from "components/ProgramSelect"
import TermSelect from "components/TermSelect"
import {KlassDropdown} from "components/Select"
import {Auth} from "types/auth"
import {majorService, settingAcademicTrackService, termsService, userService} from "services"
import {formatDateOnly, getProgramsOfTermInfo, handleError, toastError, toDateOnlyString} from "helpers"
import {useModel} from "hooks"
import {shouldConfirmAttendanceDelete} from "helpers/studentProgramDates"
import DeleteAttendanceBeforeStartDatePopup from "./DeleteAttendanceBeforeStartDatePopup"
import styles from "./StudentProgramChangePopup.module.css"

export type StudentProgramChangeMode = "major" | "majorVersion"

type Props = {
    isVisible: boolean
    mode: StudentProgramChangeMode
    activeProfile?: Auth.Profile
    student?: Auth.DepartmentStudent
    onClose: () => void
    onSuccess: () => Promise<void> | void
}

const StudentProgramChangePopup = ({isVisible, mode, activeProfile, student, onClose, onSuccess}: Props) => {
    const model = useModel()
    const userDateFormat = model.getUserDateFormat()
    const [major, setMajor] = useState<any>(null)
    const [majorVersion, setMajorVersion] = useState<any>(null)
    const [startingTerm, setStartingTerm] = useState<any>(null)
    const [academicTrack, setAcademicTrack] = useState<any>(null)
    const [academicTrackOptions, setAcademicTrackOptions] = useState<any[]>([])
    const [startDate, setStartDate] = useState<string | null>(null)
    const [contractEndDate, setContractEndDate] = useState<string | null>(null)
    const [projectedEndDate, setProjectedEndDate] = useState<string | null>(null)
    const [orientationDate, setOrientationDate] = useState<string | null>(null)
    const [startDateOptions, setStartDateOptions] = useState<any[]>([])
    const [majorVersionOptions, setMajorVersionOptions] = useState<any[]>([])
    const [isLoadingVersions, setIsLoadingVersions] = useState(false)
    const [isLoadingTracks, setIsLoadingTracks] = useState(false)
    const [isLoadingDates, setIsLoadingDates] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [showAttendanceConfirm, setShowAttendanceConfirm] = useState(false)

    const previousStartDate = activeProfile?.startDate ?? null
    const campusIds = useMemo(() => map(activeProfile?.campuses ?? [], (item) => item.id), [activeProfile?.campuses])
    const resolvedProgramId = useMemo(() => {
        if (mode === "major") {
            return major?.id ?? null
        }
        return (
            (activeProfile?.currentMajor as Auth.CurrentMajor)?.programId ??
            get(activeProfile, "currentMajor.id") ??
            null
        )
    }, [mode, major?.id, activeProfile])
    const clearDates = () => {
        setStartDate(null)
        setContractEndDate(null)
        setProjectedEndDate(null)
        setOrientationDate(null)
        setStartDateOptions([])
    }

    useEffect(() => {
        if (!isVisible) return
        setMajor(mode === "major" ? null : activeProfile?.currentMajor ?? null)
        setMajorVersion(null)
        setStartingTerm(null)
        setAcademicTrack(null)
        setAcademicTrackOptions([])
        clearDates()
        setShowAttendanceConfirm(false)
        setMajorVersionOptions([])
    }, [isVisible, mode, activeProfile?.currentMajor])

    useEffect(() => {
        if (!isVisible) return
        if (!resolvedProgramId) {
            setMajorVersionOptions([])
            return
        }
        ;(async () => {
            setIsLoadingVersions(true)
            try {
                const majorVersions = await majorService.getMajorVersions({
                    orderBy: "version_name",
                    orderDir: "ASC",
                    program_id: resolvedProgramId,
                    isActive: true
                })
                setMajorVersionOptions(
                    majorVersions.map((item) => ({
                        ...item,
                        versionName: item.version_name,
                        versionCode: item.version_code
                    }))
                )
            } catch (error) {
                handleError(error)
                setMajorVersionOptions([])
            } finally {
                setIsLoadingVersions(false)
            }
        })()
    }, [isVisible, resolvedProgramId])

    const applyStartDateOption = (option?: any | null) => {
        if (!option?.id) {
            setStartDate(null)
            setContractEndDate(null)
            setProjectedEndDate(null)
            setOrientationDate(null)
            return
        }
        setStartDate(option.id)
        setContractEndDate(option.contractEndDate ? toDateOnlyString(option.contractEndDate) : null)
        setProjectedEndDate(option.projectedEndDate ? toDateOnlyString(option.projectedEndDate) : null)
        setOrientationDate(option.orientationDate ? toDateOnlyString(option.orientationDate) : null)
    }

    const refreshStartDateOptions = async (
        termId?: number | null,
        versionId?: number | null,
        trackId?: number | null
    ) => {
        if (!termId || !versionId) {
            clearDates()
            return
        }
        setIsLoadingDates(true)
        try {
            const {data: programsOfTerm} = await majorService.getMajorByTerm([termId])
            const programsByTerm = getProgramsOfTermInfo(programsOfTerm, {
                currentMajorVersionId: versionId,
                academicTrackId: trackId ?? null
            })
            const options = programsByTerm
                .map((item) =>
                    item.startDate
                        ? {
                              id: toDateOnlyString(item.startDate),
                              name: formatDateOnly(item.startDate, userDateFormat),
                              contractEndDate: item.projectedEndDate ? toDateOnlyString(item.projectedEndDate) : null,
                              projectedEndDate: item.projectedEndDate ? toDateOnlyString(item.projectedEndDate) : null,
                              orientationDate: item.orientationDate ? toDateOnlyString(item.orientationDate) : null
                          }
                        : null
                )
                .filter(Boolean)
            setStartDateOptions(options)
            applyStartDateOption(options[0] ?? null)
        } catch (error) {
            handleError(error)
            clearDates()
        } finally {
            setIsLoadingDates(false)
        }
    }

    const loadAcademicTracksForTerm = async (termId?: number | null) => {
        if (!termId) {
            setAcademicTrackOptions([])
            setAcademicTrack(null)
            return []
        }
        setIsLoadingTracks(true)
        try {
            const [{data}, term] = await Promise.all([
                settingAcademicTrackService.getAllAcademicTracks({
                    sort: {orderBy: "name", orderDir: "asc"}
                }),
                termsService.getTerm(termId)
            ])
            const academicTracksKeyById = keyBy(data, "trackId")
            const tracks = (term.academicTrackIds || [])
                .map((academicTrackId) => {
                    const track = academicTracksKeyById[academicTrackId]
                    if (track) {
                        return {trackId: track.trackId, name: track.name}
                    }
                    return null
                })
                .filter(Boolean)
            setAcademicTrackOptions(tracks)
            return tracks
        } catch (error) {
            handleError(error)
            setAcademicTrackOptions([])
            return []
        } finally {
            setIsLoadingTracks(false)
        }
    }

    const onChangeMajor = (value) => {
        setMajor(value)
        setMajorVersion(null)
        setStartingTerm(null)
        setAcademicTrack(null)
        setAcademicTrackOptions([])
        clearDates()
    }

    const onChangeMajorVersion = (value) => {
        setMajorVersion(value)
        setStartingTerm(null)
        setAcademicTrack(null)
        setAcademicTrackOptions([])
        clearDates()
    }

    const onChangeStartingTerm = async (value) => {
        setStartingTerm(value)
        setAcademicTrack(null)
        clearDates()
        if (!value?.id || !majorVersion?.id) {
            setAcademicTrackOptions([])
            return
        }
        const tracks = await loadAcademicTracksForTerm(value.id)
        if (!tracks.length) {
            await refreshStartDateOptions(value.id, majorVersion.id, null)
            return
        }
        if (tracks.length === 1) {
            setAcademicTrack(tracks[0])
            await refreshStartDateOptions(value.id, majorVersion.id, tracks[0].trackId)
        }
    }

    const onChangeAcademicTrack = async (value) => {
        setAcademicTrack(value)
        await refreshStartDateOptions(startingTerm?.id, majorVersion?.id, value?.trackId ?? null)
    }

    const onChangeStartDate = (option) => {
        applyStartDateOption(option)
    }

    const buildPayload = (deleteDailyAttendanceBeforeStartDate?: boolean) => {
        const payload: Record<string, any> = {
            type: student?.type || activeProfile?.type,
            currentMajorVersionId: majorVersion?.id ?? null,
            startingTermId: startingTerm?.id ?? null,
            academicTrackId: academicTrack?.trackId ?? null,
            startDate: startDate ?? null,
            contractEndDate: startDate ? contractEndDate : null,
            projectedEndDate: startDate ? projectedEndDate : null,
            orientationDate: startDate ? orientationDate : null
        }
        if (mode === "major") {
            payload.currentMajor = major?.id ?? null
        }
        if (deleteDailyAttendanceBeforeStartDate) {
            payload.deleteDailyAttendanceBeforeStartDate = true
        }
        return payload
    }

    const validate = () => {
        if (mode === "major" && !major?.id) {
            toastError("Please select a program")
            return false
        }
        if (mode === "majorVersion" && !majorVersion?.id) {
            toastError("Please select a program version")
            return false
        }
        if ((startingTerm?.id || startDate) && !majorVersion?.id) {
            toastError("Please select a program version")
            return false
        }
        if (startDate && !startingTerm?.id) {
            toastError("Please select a starting term")
            return false
        }
        if (startingTerm?.id && academicTrackOptions.length && !academicTrack?.trackId && startDate) {
            toastError("Please select an academic track")
            return false
        }
        return true
    }

    const submit = async (deleteDailyAttendanceBeforeStartDate?: boolean) => {
        const profileId = student?.profileId || activeProfile?.id
        if (!profileId || !validate()) return
        setIsSubmitting(true)
        try {
            await userService.updateProfileUserById(profileId, buildPayload(deleteDailyAttendanceBeforeStartDate))
            await onSuccess()
            setShowAttendanceConfirm(false)
            onClose()
        } catch (error) {
            handleError(error)
        } finally {
            setIsSubmitting(false)
        }
    }

    const onClickSave = async () => {
        if (!validate()) return
        if (shouldConfirmAttendanceDelete(previousStartDate, startDate)) {
            setShowAttendanceConfirm(true)
            return
        }
        await submit(false)
    }

    const title = mode === "major" ? "Update Program" : "Update Program Version"

    return (
        <>
            <BasePopup isShow={isVisible && !showAttendanceConfirm} onClose={onClose} leftIcon="EDIT" width="520px">
                <div className={styles.root}>
                    <div className={styles.title}>{title}</div>
                    <div className={styles.body}>
                        {mode === "major"
                            ? "Program is required. Leave other fields empty to clear existing values."
                            : "Program version is required. Leave other fields empty to clear existing values."}
                    </div>
                    <div className={styles.form}>
                        {mode === "major" ? (
                            <div className={styles.field}>
                                <div className={styles.label}>Program</div>
                                <ProgramSelect
                                    isActive
                                    value={major}
                                    campusId={campusIds}
                                    onChange={onChangeMajor}
                                    placeholder="Select program"
                                />
                            </div>
                        ) : null}
                        <div className={styles.field}>
                            <div className={styles.label}>Program Version</div>
                            <KlassDropdown
                                isClearable={mode === "major"}
                                options={majorVersionOptions}
                                isLoading={isLoadingVersions}
                                isDisabled={mode === "major" ? !major?.id : false}
                                getOptionLabel={({versionCode, versionName}) =>
                                    versionCode ? `${versionName} (${versionCode})` : versionName
                                }
                                getOptionValue={({id}) => id}
                                value={majorVersion}
                                onChange={onChangeMajorVersion}
                                placeholder={mode === "major" ? "Select version (optional)" : "Select version"}
                            />
                        </div>
                        <div className={styles.field}>
                            <div className={styles.label}>Starting Term</div>
                            <TermSelect
                                isClearable
                                value={startingTerm}
                                majorId={resolvedProgramId}
                                majorVersionId={majorVersion?.id}
                                isDisabled={!majorVersion?.id}
                                onChange={onChangeStartingTerm}
                                placeholder="Select starting term (optional)"
                            />
                        </div>
                        <div className={styles.field}>
                            <div className={styles.label}>Academic Track</div>
                            <KlassDropdown
                                isClearable
                                options={academicTrackOptions}
                                isLoading={isLoadingTracks}
                                isDisabled={!startingTerm?.id || !academicTrackOptions.length}
                                valueKey="trackId"
                                labelKey="name"
                                value={academicTrack}
                                onChange={onChangeAcademicTrack}
                                placeholder={
                                    !startingTerm?.id
                                        ? "Select starting term first"
                                        : academicTrackOptions.length
                                        ? "Select academic track (optional)"
                                        : "No tracks for this term"
                                }
                            />
                        </div>
                        <div className={styles.field}>
                            <div className={styles.label}>Start Date</div>
                            <KlassDropdown
                                isClearable
                                options={startDateOptions}
                                isLoading={isLoadingDates}
                                isDisabled={
                                    !startingTerm?.id ||
                                    isLoadingDates ||
                                    (academicTrackOptions.length > 0 && !academicTrack?.trackId)
                                }
                                value={startDate ? startDateOptions.find((item) => item.id === startDate) : undefined}
                                onChange={onChangeStartDate}
                                placeholder={
                                    !startingTerm?.id
                                        ? "Select starting term first"
                                        : academicTrackOptions.length > 0 && !academicTrack?.trackId
                                        ? "Select academic track first"
                                        : "Select start date (optional)"
                                }
                            />
                        </div>
                    </div>
                    <div className={styles.footer}>
                        <SecondaryButton title="Cancel" onClick={onClose} disabled={isSubmitting} />
                        <BaseButton
                            title="Save"
                            onClick={onClickSave}
                            loading={isSubmitting || isLoadingDates || isLoadingTracks}
                            disabled={isLoadingVersions}
                        />
                    </div>
                </div>
            </BasePopup>

            <DeleteAttendanceBeforeStartDatePopup
                isVisible={isVisible && showAttendanceConfirm}
                previousStartDate={previousStartDate}
                newStartDate={startDate}
                loading={isSubmitting}
                onClose={() => setShowAttendanceConfirm(false)}
                onConfirmNo={() => submit(false)}
                onConfirmYes={() => submit(true)}
            />
        </>
    )
}

export default StudentProgramChangePopup
